import { get, post, put } from '@/api/apiService.ts';
import type {
  CreateSessionRequest,
  AddSessionMessageRequest,
  CompleteSessionRequest,
  RecordSessionEventRequest,
  SessionEventResponse,
  SessionResponse,
  SessionMessageResponse,
  SessionDetailResponse,
  PaginatedSessionsResponse,
  TeamStatsResponse,
  TeamRankingsResponse,
  MyRankingResponse,
  MyProgressStatsResponse,
} from '@/types/session.ts';

const sessionService = {
  create(data: CreateSessionRequest): Promise<SessionResponse> {
    return post<CreateSessionRequest, SessionResponse>('/api/sessions', data);
  },

  addMessage(sessionId: string, data: AddSessionMessageRequest): Promise<SessionMessageResponse> {
    return post<AddSessionMessageRequest, SessionMessageResponse>(
      `/api/sessions/${sessionId}/messages`,
      data,
    );
  },

  complete(sessionId: string, data: CompleteSessionRequest): Promise<SessionDetailResponse> {
    return put<CompleteSessionRequest, SessionDetailResponse>(
      `/api/sessions/${sessionId}/complete`,
      data,
    );
  },

  getMine(page = 1, pageSize = 20): Promise<PaginatedSessionsResponse> {
    return get<PaginatedSessionsResponse>(`/api/sessions/mine?page=${page}&pageSize=${pageSize}`);
  },

  getById(sessionId: string): Promise<SessionDetailResponse> {
    return get<SessionDetailResponse>(`/api/sessions/${sessionId}`);
  },

  getTeam(page = 1, pageSize = 20): Promise<PaginatedSessionsResponse> {
    return get<PaginatedSessionsResponse>(`/api/sessions/team?page=${page}&pageSize=${pageSize}`);
  },

  getTeamStats(teamId?: string): Promise<TeamStatsResponse> {
    const query = teamId ? `?teamId=${teamId}` : '';
    return get<TeamStatsResponse>(`/api/sessions/team/stats${query}`);
  },

  getMyRanking(): Promise<MyRankingResponse> {
    return get<MyRankingResponse>('/api/sessions/my-ranking');
  },

  getMyStats(): Promise<MyProgressStatsResponse> {
    return get<MyProgressStatsResponse>('/api/sessions/my-stats');
  },

  getTeamRankings(): Promise<TeamRankingsResponse> {
    return get<TeamRankingsResponse>('/api/sessions/team-rankings');
  },

  regenerateReport(sessionId: string): Promise<void> {
    return post<undefined, void>(`/api/sessions/${sessionId}/report/regenerate`, undefined);
  },

  recordEvent(sessionId: string, data: RecordSessionEventRequest): Promise<SessionEventResponse> {
    return post<RecordSessionEventRequest, SessionEventResponse>(
      `/api/sessions/${sessionId}/events`,
      data,
    );
  },

  getEvents(sessionId: string): Promise<SessionEventResponse[]> {
    return get<SessionEventResponse[]>(`/api/sessions/${sessionId}/events`);
  },
};

export default sessionService;
